import * as d3 from 'd3';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { io } from 'socket.io-client';
import { SessionDataEntry } from '../types/api';

const LIVE_WINDOW_SEC = 90;
const HIGHLIGHT_DURATION_MS = 2000;

type ChannelPoint = { timestamp: number; duration: number; value: number };
type ChannelData = Record<string, ChannelPoint[]>;

function processEntryIntoChannels(
  entry: SessionDataEntry,
  prev: { lastFog: number; validLFN: boolean }
): { points: Partial<Record<string, ChannelPoint>>; lastFog: number; validLFN: boolean } {
  const t = entry._t;
  const d = entry._d ?? 0;
  const points: Partial<Record<string, ChannelPoint>> = {};
  let { lastFog, validLFN } = prev;

  if (entry.HR !== undefined && entry.HR !== 0) {
    const value = Array.isArray(entry.HR)
      ? entry.HR.reduce((s, x) => s + x, 0) / entry.HR.length
      : entry.HR;
    points.HR = { timestamp: t, duration: d, value };
  }
  if (entry.HRV !== undefined && entry.HRV !== 0) {
    const value = Array.isArray(entry.HRV)
      ? entry.HRV.reduce((s, x) => s + x, 0) / entry.HRV.length
      : entry.HRV;
    points.HRV = { timestamp: t, duration: d, value };
  }
  if (entry.Fog !== undefined) {
    const fogValue = Array.isArray(entry.Fog)
      ? entry.Fog.reduce((s, x) => s + x, 0) / entry.Fog.length
      : entry.Fog;
    lastFog = fogValue * 100;
    points.Fog = { timestamp: t, duration: d, value: lastFog };
  } else if (entry.HRV !== undefined && entry.HRV > 0) {
    // If we have HRV data but no fog yet, use last fog data.
    points.Fog = { timestamp: t, duration: d, value: lastFog };
  }
  if (entry.LFN !== undefined) {
    let lfnValue = Array.isArray(entry.LFN)
      ? entry.LFN.reduce((s, x) => s + x, 0) / entry.LFN.length
      : entry.LFN;
    if (!validLFN) {
      if (lfnValue < 97) validLFN = true;
      else return { points, lastFog, validLFN };
    }
    points['LF/HF'] = {
      timestamp: t,
      duration: d,
      value: lfnValue > 0 ? lfnValue / (100 - lfnValue) : 0,
    };
  }

  return { points, lastFog, validLFN };
}

function trimToWindow(data: ChannelData, windowSec: number): ChannelData {
  const channels = ['HR', 'HRV', 'LF/HF', 'Fog'] as const;
  const allTs = channels.flatMap(c => (data[c] || []).map(p => p.timestamp));
  const maxT = allTs.length ? Math.max(...allTs) : 0;
  const minT = maxT - windowSec;
  const out: ChannelData = {};
  for (const c of channels) {
    const arr = data[c] || [];
    out[c] = arr.filter(p => p.timestamp >= minT);
  }
  return out;
}

export interface LiveSessionEvent {
  id: number;
  text: string;
  timestamp: number;
  highlighted: boolean;
}

const FLAME_EASE_DURATION_MS = 3000;

export interface LiveSessionBlockHandle {
  addLiveData: (entry: SessionDataEntry[]) => void;
  addEvent: (text: string) => void;
  /** Set flame deviation (-1 to 1). Bar eases back to 0 over 3s; each call resets that ease. */
  setFlameHeight: (value: number) => void;
}

export interface LiveSessionBlockProps {
  /** When set, a socket.io client connects to stream session data and events. */
  sessionId?: string;
  /** Session start time (ISO string or Date). Used to compute _t when the server omits it. */
  sessionStart?: string | Date;
  /** Auth token for the socket handshake. If omitted, uses localStorage.getItem('authToken'). */
  authToken?: string;
  /** Callback function to be called when the session is complete. */
  onComplete?: () => void;
}

const CHANNELS = ['HR', 'HRV', 'LF/HF', 'Fog'] as const;
const CHANNEL_COLORS: Record<string, string> = {
  HR: '#e74c3c',
  HRV: '#2ecc71',
  'LF/HF': '#3498db',
  Fog: '#95a5a6',
};

const emptyChannelData: ChannelData = {
  HR: [],
  HRV: [],
  'LF/HF': [],
  Fog: [],
};

export const LiveSessionBlock = forwardRef<LiveSessionBlockHandle, LiveSessionBlockProps>(
  function LiveSessionBlock({ sessionId, sessionStart, authToken, onComplete }, ref) {
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [channelData, setChannelData] = useState<ChannelData>(emptyChannelData);
    const [events, setEvents] = useState<LiveSessionEvent[]>([]);
    const processState = useRef({ lastFog: 0, validLFN: false });
    const nextEventId = useRef(0);
    const addLiveData = useCallback((entries: SessionDataEntry[]) => {
      setChannelData(prev => {
        const processed: Partial<Record<string, ChannelPoint>>[] = [];
        let runningState = { ...processState.current };
        entries.sort((a, b) => a._t - b._t);
        for ( const entry of entries ) {
          const { points, lastFog, validLFN } = processEntryIntoChannels(
            entry,
            runningState
          );
          runningState = { lastFog, validLFN };
          processed.push(points);
        }
        processState.current = runningState;
        const next: ChannelData = { ...emptyChannelData };
        for (const c of CHANNELS) {
          const arr = [...(prev[c] || [])];
          const pts = processed.map(p => p[c]).filter(p => p !== undefined);
          if (pts && pts.length > 0) arr.push.apply( arr, pts as ChannelPoint[] );
          next[c] = arr;
        }
        return trimToWindow(next, LIVE_WINDOW_SEC);
      });
    }, []);

    const addEvent = useCallback((text: string | { text: string; timestamp: number }[]) => {
      const events = Array.isArray(text) ? text : [{ text, timestamp: Date.now() }];
      const newEvents: LiveSessionEvent[] = [];
      const ids: number[] = [];
      // Put most recent events first
      events.sort((a, b) => b.timestamp - a.timestamp);
      for (const event of events) {
        const id = ++nextEventId.current;
        newEvents.push({ id, text: event.text, timestamp: event.timestamp, highlighted: true });
        ids.push(id);
      }
      setEvents(prev => newEvents.concat(prev));
      setTimeout(() => {
        setEvents(prev =>
          prev.map(e => (ids.includes(e.id) ? { ...e, highlighted: false } : e))
        );
      }, HIGHLIGHT_DURATION_MS);
    }, []);

    const flameEaseRef = useRef<{ rafId: number | null; startValue: number; startTime: number }>({
      rafId: null,
      startValue: 0,
      startTime: 0,
    });
    const [flameHeight, setFlameHeightState] = useState(0);

    const setFlameHeight = useCallback((value: number) => {
      const clamped = Math.max(-1, Math.min(1, value));
      const ref = flameEaseRef.current;
      if (ref.rafId != null) cancelAnimationFrame(ref.rafId);
      ref.startValue = clamped;
      ref.startTime = Date.now();
      setFlameHeightState(clamped);

      const tick = () => {
        const elapsed = Date.now() - ref.startTime;
        const t = Math.min(1, elapsed / FLAME_EASE_DURATION_MS);
        const eased = 1 - (1 - t) * (1 - t);
        const current = ref.startValue * (1 - eased);
        setFlameHeightState(current);
        if (t < 1) {
          ref.rafId = requestAnimationFrame(tick);
        } else {
          ref.rafId = null;
        }
      };
      ref.rafId = requestAnimationFrame(tick);
    }, []);

    useEffect(() => {
      // This value may have changed by the time the cleanup is called, so capture it here.
      const easeRef = flameEaseRef.current;
      return () => {
        if (easeRef.rafId != null) {
          cancelAnimationFrame(easeRef.rafId);
        }
      };
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        addLiveData,
        addEvent,
        setFlameHeight,
      }),
      [addLiveData, addEvent, setFlameHeight]
    )
    const [activeChannels, setActiveChannels] = useState({
      HR:      true,
      HRV:     true,
      'LF/HF': true,
      Fog:     true,
    });

    const toggleChannel = (channel: string) => {
      setActiveChannels(prev => ({
        ...prev,
        [channel]: !prev[channel as keyof typeof prev]
      }));
    };

    const hasAnyData = CHANNELS.some(c => (channelData[c] || []).length > 0);
    const [containerWidth, setContainerWidth] = useState(0);
    const chartHeight = 230;
    const chartWidth = containerWidth > 0 ? containerWidth : 420;

    useEffect(() => {
      const updateWidth = () => {
        if (containerRef.current) {
          const windowWidth = window.innerWidth;
          const chartContainerWidth = containerRef.current.clientWidth;
          const boundingRect = containerRef.current.getBoundingClientRect();
          const padding = boundingRect.left + ( document.documentElement.scrollWidth - boundingRect.right );
          const availableWidth = Math.max(windowWidth, chartContainerWidth) - padding;
          setContainerWidth(Math.max( availableWidth, 640 ));
        }
      };
      updateWidth();
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }, [hasAnyData, channelData]);

    useEffect(() => {
      if (!svgRef.current || !hasAnyData) return;
      d3.select(svgRef.current).selectAll('*').remove();

      const margin = { top: 0, right: 10, bottom: 28, left: 10 };
      const allTs = CHANNELS.flatMap(c => (channelData[c] || []).map(p => p.timestamp));
      const maxT = Math.max(...allTs);
      const minT = Math.max(0, maxT - LIVE_WINDOW_SEC);
      const xScale = d3.scaleLinear().domain([minT, maxT]).range([0, chartWidth - margin.left - margin.right]);
      const chartH = chartHeight - margin.top - margin.bottom;

      const yExtents: Record<string, [number, number]> = {};
      for (const c of CHANNELS) {
        const arr = channelData[c] || [];
        if (arr.length) {
          const ext = d3.extent(arr, d => d.value) as [number, number];
          if (c === 'Fog') yExtents[c] = [0, 100];
          else if (c === 'LF/HF') yExtents[c] = [0, (ext[1] ?? 0) + 1];
          else yExtents[c] = [(ext[0] ?? 0) - 5, (ext[1] ?? 0) + 5];
        }
      }
      const yScales: Record<string, d3.ScaleLinear<number, number>> = {};
      for (const c of CHANNELS) {
        const ext = yExtents[c];
        if (ext) {
          yScales[c] = d3.scaleLinear().domain(ext).range([chartH, 0]);
        }
      }

      const svg = d3
        .select(svgRef.current)
        .attr('width', chartWidth)
        .attr('height', chartHeight)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // const plotW = chartWidth - margin.left - margin.right;

      // Draw Fog channel first (as shaded area)
      const fogArea = d3.area<{ timestamp: number; value: number }>()
        .x(d => xScale(d.timestamp))
        .y0(chartH)
        .y1(d => yScales.Fog(d.value))
        .curve( d3.curveMonotoneX);

      svg.append('path')
        .datum(channelData['Fog'])
        .attr('fill', CHANNEL_COLORS.Fog)
        .attr('fill-opacity', activeChannels.Fog ? 0.2 : 0.05)
        .attr('d', fogArea);

      const fogLine = d3.line<{ timestamp: number; value: number }>()
        .x(d => xScale(d.timestamp))
        .y(d => yScales.Fog(d.value))
        .curve(d3.curveMonotoneX);

      svg.append('path')
        .datum(channelData['Fog'])
        .attr('fill', 'none')
        .attr('stroke', CHANNEL_COLORS.Fog)
        .attr('stroke-width', 1.5)
        .attr('opacity', activeChannels.Fog ? 0.5 : 0.15)
        .attr('d', fogLine);

      // Line generators for other channels
      const channels = ['HR', 'HRV', 'LF/HF'];
      for (const c of channels) {
        const arr = channelData[c] || [];
        const y = yScales[c];
        if (!y || !arr.length) continue;
        const line = d3
          .line<ChannelPoint>()
          .x(d => xScale(d.timestamp))
          .y(d => y(d.value))
          .curve(d3.curveMonotoneX);
        svg
          .append('path')
          .datum(arr)
          .attr('fill', 'none')
          .attr('stroke', CHANNEL_COLORS[c])
          .attr('stroke-width', 2)
          .attr('opacity', activeChannels[c as keyof typeof activeChannels] ? 1 : 0.15)
          .attr('d', line);
      }

      const xAxis = d3.axisBottom(xScale).ticks(6);
      svg
        .append('g')
        .attr('transform', `translate(0,${chartH})`)
        .call(xAxis);
    }, [activeChannels, channelData, hasAnyData, chartWidth, chartHeight, containerWidth]);

    const [spinnerIndex, setSpinnerIndex] = useState(0);
    // Add useMemo to memoize the spinners array
    const spinners = useMemo(() => ['⣷', '⣯', '⣟', '⡿', '⢿', '⣻', '⣽', '⣾'], []);
    const lastMessageRef = useRef(Date.now());
    const [isConnected, setIsConnected] = useState(false);
    const [isStale, setIsStale] = useState(false);

    useEffect(() => {
      if (!sessionId) return;
      const sessionStartMs = sessionStart
        ? (typeof sessionStart === 'string' ? new Date(sessionStart).getTime() : sessionStart.getTime())
        : Date.now();
      const dataKeys = ['HR', 'HRV', 'Fog', 'LFN'];
      // Matches enum in unity://XRegulation/Assets/NeuroProgeny/Scripts/SessionScore.cs
      const achievements = [
        'First Recovery',
        'Triple Threat',
        'Optimal Master',
        'Regulation Hero',
      ];
      const token = authToken ?? localStorage.getItem('authToken') ?? '';
      const baseUrl = new URL( process.env.REACT_APP_API_URL ?? '' );
      const streamUrl = `${baseUrl.protocol === 'https:' ? 'wss:' : 'ws:'}//${baseUrl.hostname}${baseUrl.port ? `:${baseUrl.port}` : ''}/session-data?sessionid=${sessionId}`;
      let firstData = false;
      let interpolateFog = true;
      let wasConnected = false;
      // let previousDeviation: number | undefined = undefined;
      const eventEmoji = {
        "Achievement": "🎉",
        "Candle": "🕯️",
        "Connected": "🔗",
        "Disconnected": "❌",
        "Fire Lit": "🔥",
        "Match Lit": "🔥",
        "Matchbox": "🔥",
        "Recovery": "💪",
        "Session Start": "📈",
        "Stick": "🪵",
        "Sunset": "🌅",
        "UserEnd": "✅",
      }
      let allHeartCount = 0;
      let allHeartTotal = 0;

      const spinMe = () => {
        lastMessageRef.current = Date.now();
        setIsStale(false);
        setSpinnerIndex(prev => (prev + 1) % spinners.length);
      };

      // Optional: detect stale connection
      const staleCheck = setInterval(() => {
        if (Date.now() - lastMessageRef.current > 3_000) {
          setIsStale(true);
        }
      }, 1000);

      const socket = io(
        streamUrl,
        {
          path: '/api/v1/stream',
          auth: { token },
        }
      );
      socket.on('connect', () => {
        addEvent( wasConnected ? 'Reconnected to server' : 'Connected to server' );
        setIsConnected(true);
      });
      socket.on('connect_error', (error: unknown) => {
        error??= "Unknown error";
        addEvent(`session connection error: ${error}`);
        setIsConnected(false);
      });
      socket.on('disconnect', (reason: string) => {
        clearInterval(staleCheck);
        addEvent(`Disconnected from server: ${reason}`);
        setIsConnected(false);
      });
      socket.on('close', (msg: unknown) => {
        addEvent(`Stream has closed: ${msg}`);
        socket.disconnect();
      });
      socket.on('status', (msg: any) => {
        // Will be: { "status": "string", "message": "string", "sessionId": "string" }
        spinMe();
        if ( msg.status === "OK" ) {
          if ( msg.message === 'Joined session data stream as reader' ) {
            if ( !wasConnected ) {
              wasConnected = true;
              addEvent('Waiting for headset data...');
            } else {
              addEvent('Resumed data stream');
            }
          } else {
            addEvent( msg.message );
          }
        }
      });
      socket.on('data', (data: any) => {
        spinMe();
        if ( !firstData ) {
          firstData = true;
          addEvent('Data stream is live');
        }
        const dataQueue = Array.isArray(data) ? data : [data];
        const dataEntries: SessionDataEntry[] = [];
        const events: { text: string; timestamp: number }[] = [];
        let   ts = new Date();
        const flameDev = { value: 0, found: false }
        const queueEvent = (text: string) => {
          events.push({ text, timestamp: ts.getTime() });
        };
        for (const item of dataQueue) {
          const entry = item as SessionDataEntry;
          // Set _t = seconds since session start when server omits it
          ts = item.ts && typeof item.ts === 'string' ? new Date(item.ts) : new Date();
          entry._t = ( ts.getTime() - sessionStartMs ) / 1_000;
          if ( allHeartTotal === 0 ) {
            for ( const key of ['HR', 'HRV'] ) {
              if ( entry[key] !== undefined ) {
                allHeartTotal += Array.isArray(entry[key]) ? entry[key].reduce((s: number, x: number) => s + x, 0) : entry[key];
                allHeartCount++;
              }
            }
          }
          // Copy HR, HRV, Fog, LFN to dataEntry
          let dataEntry: Partial<SessionDataEntry> = {
            _t: entry._t,
          }
          let found = false;
          for (const key of dataKeys) {
            if (entry[key] !== undefined) {
              dataEntry[key] = entry[key];
              found = true;
            }
          }
          if (found) {
            if ( interpolateFog ) {
              if ( entry.Fog !== undefined ) {
                interpolateFog = false;
              } else if ( entry.LFN !== undefined ) {
                // Interpolate Fog from LFN
                const lfnValue = Array.isArray(entry.LFN)
                  ? entry.LFN.reduce((s, x) => s + x, 0) / entry.LFN.length
                  : entry.LFN;
                let freqRatio = lfnValue > 0 ? lfnValue / (100 - lfnValue) : 0;
                const minFog = 1.3;
                const maxFog = 5.0;
                freqRatio = Math.min( Math.max(freqRatio, minFog), maxFog);
                dataEntry.Fog = ( freqRatio - minFog ) / ( maxFog - minFog ); // Inverse lerp
                interpolateFog = false;
              }
            }
            dataEntries.push(dataEntry as SessionDataEntry);
          }

          if ( entry.BTConnect !== undefined ) {
            queueEvent(`HRV Monitor Connected`);
          }

          // Look for any non-data keys to add to the events:
          if ( entry.VR !== undefined ) {
            const vrEvent = entry.VR;
            if ( /^C\d+$/.test(vrEvent) ) {
              const candle = parseInt(vrEvent.substring(1));
              queueEvent(`${candle} minute candle extinguished 🕯️`);
            } else if ( vrEvent.indexOf('Recovery') !== -1 ) {
              const recoveryType = vrEvent.substring(0, vrEvent.indexOf('Recovery'));
              queueEvent(`${recoveryType === 'LFHF' ? 'LF/HF' : recoveryType} Recovery 💪`);
            } else if ( vrEvent === 'SessionStart' ) {
              queueEvent('First biometric data acquired 📈');
            } else if ( vrEvent === 'MatchBoxBurn' ) {
              queueEvent( "🔥 Matchbox burned! 🔥")
            } else {
              // Split vrEvent at CamelCaseBoundaries and reattach with spaces
// TODO: Make sure vrEvent is a string before we try to do anything to it.
              let eventText = vrEvent.replace(/([A-Z][a-z])/g, ' $1').trim();
              for (const [key, emoji] of Object.entries(eventEmoji)) {
                if ( eventText.indexOf(key) !== -1 ) {
                  eventText += ` ${emoji}`;
                }
              }
              queueEvent( eventText );
            }
          }

          if ( entry.FlameDev !== undefined ) {
            flameDev.value = entry.FlameDev;
            flameDev.found = true;
          }

          if ( entry.Achievement !== undefined ) {
            // Use nemeric as lookup, otherwise use text
            let achievementText = typeof entry.Achievement === 'number' ? achievements[entry.Achievement] : entry.Achievement;
            achievementText = achievementText.replace(/([a-z])([A-Z])/g, '$1 $2');
            queueEvent( `<span class="bg-blue-100 text-blue-800">Achievement: ${achievementText} 🎉</span>` );
          }

          if ( entry.SoC !== undefined ) {
            const batteryEmoji = entry.SoC < 20 ?  '🪫' : '🔋';
            queueEvent( `Sensor Battery: ${entry.SoC}% ${batteryEmoji}` );
          }

          if ( entry.UserEnd !== undefined ) {
            onComplete?.();
            // Show's over, folks!  Refresh the page in 5 seconds.
            queueEvent( '✅ User ended session. Refreshing in 5 seconds... 🔄' );
            // Loaded page will also need to call refresh data
            setTimeout(() => {
              // Reload page to switch from live to completed.
              window.location.reload();
            }, 5000); // 5 seconds
          }
        };
        addLiveData(dataEntries);
        if ( allHeartTotal === 0 && allHeartCount > 0 ) {
          allHeartTotal = -1;
          queueEvent( `HRV sensor calibrating... ❤️` );
        }
        addEvent(events); // This will handle the sorting for us
        if (flameDev.found) setFlameHeight(flameDev.value);
      });
      socket.on('disconnect', () => {
        addEvent('Disconnected from server');
      });
      // Cleanup function
      return () => {
        socket.disconnect();
      };
    }, [
      sessionId,
      sessionStart,
      authToken,
      addEvent,
      addLiveData,
      onComplete,
      setFlameHeight,
      spinners.length,
    ]);

    return (
      <div className="mt-6 pt-6 border-t border-secondary-200">
        <div className="flex gap-4">
          <div
            ref={containerRef}
            className="flex-1 min-w-0 rounded-lg border border-secondary-200 bg-secondary-50/50 overflow-hidden"
            style={{ minHeight: chartHeight }}
          >
            <div className="px-3 pt-2 pb-1">
              <h3 className="text-sm font-semibold text-secondary-700">
                Live (last {LIVE_WINDOW_SEC}s)
              </h3>
            </div>
            {hasAnyData ? (
              <svg ref={svgRef} className="block" />
            ) : (
              <div
                className="flex items-center justify-center text-secondary-400 text-sm px-4"
                style={{ height: chartHeight - 40 }}
              >
                Waiting for live data…
              </div>
            )}
          </div>
          <div className="w-28 flex-shrink-0 rounded-lg border border-secondary-200 bg-secondary-50/50 flex flex-col py-2 px-2">
            <h3 className="text-xs font-semibold text-secondary-700 mb-2 px-0.5">Channels</h3>
            <table className="w-full text-xs border-collapse">
              <tbody>
                {CHANNELS.map(channel => {
                  const arr = channelData[channel] ?? [];
                  const lastPoint = arr.length > 0 ? arr[arr.length - 1] : null;
                  const value = lastPoint?.value;
                  const format =
                    channel === 'LF/HF'
                      ? (v: number) => v.toFixed(1)
                      : channel === 'Fog'
                      ? (v: number) => `${v.toFixed(0)}%`
                      : (v: number) => v.toFixed(0);
                  return (
                    <tr key={channel} className="leading-tight">
                      <td
                        className="py-0.5 pr-2 font-medium truncate align-baseline cursor-pointer hover:bg-secondary-100/50 p-1 rounded transition-colors"
                        style={{ color: CHANNEL_COLORS[channel], opacity: activeChannels[channel as keyof typeof activeChannels] ? 1 : 0.3 }}
                        onClick={() => toggleChannel(channel)}
                      >
                        {channel}
                      </td>
                      <td
                        className="py-0.5 w-12 text-right font-mono tabular-nums text-secondary-600 align-baseline whitespace-nowrap"
                        style={{ opacity: activeChannels[channel as keyof typeof activeChannels] ? 1 : 0.3 }}
                      >
                        {value != null ? format(value) : '—'}
                      </td>
                    </tr>
                  );
                })}
                <tr>
                  <td className="py-0.5 pr-2 font-medium truncate align-middle p-1 rounded transition-colors" style={{ color: 'darkred' }} >
                    Flame
                  </td>
                  <td className="py-0.5 w-12 align-middle">
                    <div className="relative h-14 w-full rounded border border-secondary-300 bg-secondary-100">
                      <div
                        className="absolute left-0 right-0 h-px bg-secondary-500 z-10"
                        style={{ top: '50%', transform: 'translateY(-50%)' }}
                      />
                      {flameHeight !== 0 && (
                        <div
                          className="absolute left-0.5 right-0.5 rounded-sm z-0"
                          style={{
                            height: `${Math.abs(flameHeight) * 50}%`,
                            ...(flameHeight > 0
                              ? { bottom: '50%', backgroundColor: '#22c55e' }
                              : { top: '50%', backgroundColor: '#ef4444' }),
                          }}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="w-72 flex-shrink-0 rounded-lg border border-secondary-200 bg-secondary-50/50 flex flex-col max-h-64">
            <div className="px-3 pt-2 pb-1 border-b border-secondary-200 flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-secondary-700">Events</h3>
              <span
                className="text-lg leading-none"
                style={{
                  fontFamily: 'monospace',
                  opacity: isStale ? 0.3 : 1,
                  color: isStale ? '#9ca3af' : '#15803d',
                  transition: 'opacity 0.3s',
                }}
                title={isStale ? 'No data received recently' : 'Receiving data'}
              >
                { isConnected ? spinners[spinnerIndex] : '❌' }
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {events.length === 0 ? (
                <p className="text-sm text-secondary-400 py-2">No events yet.</p>
              ) : (
                <ul className="space-y-1.5">
                  {events.map(e => (
                    <li
                      key={e.id}
                      className={`text-sm rounded px-2 py-1.5 transition-colors duration-300 ${
                        e.highlighted
                          ? 'bg-primary-100 text-primary-900 ring-1 ring-primary-200'
                          : 'text-secondary-700 bg-secondary-100/50'
                      }`}
                    >
                      <div
                        className="break-words"
                        dangerouslySetInnerHTML={{ __html: e.text }}
                      />
                      <div className="text-secondary-500 font-mono text-[10px] mt-0.5">
                        {new Date(e.timestamp).toLocaleTimeString()}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);
